import FilterIcon from "@assets/icons/filter.svg";
import { useI18n } from "@core/i18n/i18n";
import { analyticsService, libraryFilterManager, onboardingManager } from "@core/service-container";
import { FilterCategory } from "@domain/filters/filter";
import styled from "@emotion/styled";
import { Tooltip } from "@ui/components/common/tooltip";
import { OnboardingPanel } from "@ui/components/onboarding/onboarding-panel";
import { TextInput } from "@ui/components/inputs/text-input";
import { TrackingContext } from "@ui/components/common/tracking-context-provider";
import { useExerciseLibrary } from "@ui/pages/program-builder/hooks/use-exercise-library";
import { FilterPanel } from "@ui/pages/program-builder/library/sidebar/filter-panel";
import { physioMeMobileBreakpoint } from "@ui/style";
import { colors } from "@ui/style/colors";
import { row, stack } from "@ui/style/layout";
import { useObservable } from "micro-observables";
import { OverlayPanel } from "primereact/overlaypanel";
import { OnboardingProgramQuickstartStep } from "@domain/onboarding/onboarding";
import React, { useContext, useEffect, useRef, useState } from "react";
import { AnimatedHalo } from "@ui/components/onboarding/animated-halo";
import { useExerciseFilters } from "@ui/components/exercise/hooks/use-exercise-filters";
import { hideComplementaryGuide3 } from "@ui/pages/program-builder/quickstart/complementary-tooltip-signal";
import { hideAlternativeGuide3 } from "@ui/pages/program-builder/quickstart/alternative-highlight-signal";
import { ExerciseFilter } from "@domain/library/library-filter-manager";
import { useExerciseDragAndDrop } from "@ui/pages/program-builder/hooks/use-exercise-drag-and-drop";

interface SearchFilterComponentProps {
	activeFiltersCount: number;
	showFilterLibraryGuidance?: boolean;
	onHideFilterLibraryGuidance?: () => void;
}

export const SearchFilterComponent: React.FC<SearchFilterComponentProps>
	= ({ activeFiltersCount, showFilterLibraryGuidance, onHideFilterLibraryGuidance }) => {
		const { format } = useI18n();
		const activeCategoryFilters = useObservable(libraryFilterManager.activeCategoryFilters);
		const [filterPanelIsOpened, setFilterPanelIsOpened] = useState(false);
		const exerciseFilters = useExerciseFilters();
		const filterPanelRef = useRef<OverlayPanel>(null);
		const trackingContext = useContext(TrackingContext);
		// Local state to show a title tooltip after clicking the guided Filter button
		const [showFilterTitleGuidance, setShowFilterTitleGuidance] = useState(false);
		const { dragAndDropContext } = useExerciseDragAndDrop();

		const { search } = useExerciseLibrary();

		useEffect(() => {
			const closeFilterPanelOnDragStart = () => {
				requestAnimationFrame(() => {
					if (!filterPanelIsOpened) return;
					filterPanelRef.current?.hide();
					setFilterPanelIsOpened(false);
					setShowFilterTitleGuidance(false);
				});
			};

			dragAndDropContext?.onDragStart.add(closeFilterPanelOnDragStart);
			return () => {
				dragAndDropContext?.onDragStart.remove(closeFilterPanelOnDragStart);
			};
		}, [dragAndDropContext?.onDragStart, filterPanelIsOpened]);

		return (
			<Container>
				<span className="p-input-icon-left p-input-icon-right" style={{ flex: 1 }}>
					<i className="pi pi-search" />
					<TextInput
						style={{ width: "100%" }}
						placeholder={format("exerciseLibrary.searchInputPlaceholder")}
						value={search}
						onChange={e => libraryFilterManager.searchWord.set(e.target.value)}
					/>
					<CancelFilterButton
						className="pi pi-times"
						displayed={!!search}
						onClick={() => libraryFilterManager.searchWord.set("")}
					/>
				</span>
				{showFilterLibraryGuidance ? (
					<FilterGuideTooltip
						interactive
						visible
						placement="right"
						offset={[0, 8]}
						arrow
						maxWidth={370}
						appendTo={(node: Element) => node.parentElement ?? document.body}
						onClickOutside={onHideFilterLibraryGuidance}
						content={
							<OnboardingPanel
								text={format("programBuilder.quickstart.guide.searchAndFilter")}
								onClose={() => onHideFilterLibraryGuidance?.()}
							/>
						}
					>
						<div>
							<FilterButton
								selected={activeFiltersCount > 0 || filterPanelIsOpened}
								onClick={e => {
									onHideFilterLibraryGuidance?.();
									void onboardingManager.passStepOnboarding(
										OnboardingProgramQuickstartStep.FILTER_LIBRARY_GUIDE_01,
									);

									// Show a contextual tooltip over the panel title 
									// right after clicking the guided Filter button
									setShowFilterTitleGuidance(true);
									filterPanelRef.current?.toggle(e);
								}}
							>
								<AnimatedHalo shouldDisplayHalo={showFilterLibraryGuidance}>
									<FilterIcon width={24} height={24} />
								</AnimatedHalo>
							</FilterButton>
						</div>
					</FilterGuideTooltip>
				) : (
					<FilterButton
						selected={activeFiltersCount > 0 || filterPanelIsOpened}
						onClick={e => {
							void onboardingManager.passStepOnboarding(
								OnboardingProgramQuickstartStep.FILTER_LIBRARY_GUIDE_01,
							);
							filterPanelRef.current?.toggle(e);
						}}
					>
						<FilterIcon width={24} height={24} />
					</FilterButton>
				)}
				<StyledFilterPanel
					ref={filterPanelRef}
					title={
						showFilterTitleGuidance ? (
							<FilterGuideTooltip
								interactive
								visible
								placement="top-start"
								offset={[0, 0]}
								arrow
								appendTo={() => document.body}
								onClickOutside={() => setShowFilterTitleGuidance(false)}
								content={
									<OnboardingPanel
										text={format("programBuilder.quickstart.guide.applyFilters")}
										onClose={() => setShowFilterTitleGuidance(false)}
									/>
								}
							>
								<span>{format("exerciseFilterPanel.title")}</span>
							</FilterGuideTooltip>
						) : (
							format("exerciseFilterPanel.title")
						)
					}
					appendTo={undefined}
					filterOptions={exerciseFilters}
					onShow={() => 
						setFilterPanelIsOpened(true)}
					onHide={() => {
						setFilterPanelIsOpened(false);
						setShowFilterTitleGuidance(false);
					}}
					activeFilters={activeCategoryFilters}
					onActiveFiltersChange={newActiveFilters => {
						libraryFilterManager.activeCategoryFilters.set(newActiveFilters as FilterCategory<ExerciseFilter>[]);
						hideComplementaryGuide3();
						hideAlternativeGuide3();

						// If at least one filter is applied, mark FILTER_LIBRARY_GUIDE_02 as completed
						const hasAnyFilterApplied = (newActiveFilters as FilterCategory<ExerciseFilter>[]).some(
							category => category.filters.length > 0
						);
						if (hasAnyFilterApplied) {
							void onboardingManager.passStepOnboarding(
								OnboardingProgramQuickstartStep.FILTER_LIBRARY_GUIDE_02
							);
						}
					}}
					onFilterChange={newFilter =>
						newFilter.active &&
						analyticsService.trackEvent({
							category: trackingContext.category,
							action: "filterLibrary",
							params: [`${newFilter.categoryId}/${newFilter.id.toLowerCase()}`]
						})
					}
				/>
			</Container>
		);
	};

const Container = styled.div`
	${row(8, "flexstart", "center")};
`;

const FilterButton = styled.button<{ selected: boolean }>`
	appearance: none;
	margin: 0;
	padding: 8px;
	border: none;
	position: relative;
	cursor: pointer;
	color: ${props => (props.selected ? colors.primary : colors.grey)};
	${stack(undefined, "center", "center")};
	border-radius: 8px;
	background-color: ${props => (props.selected ? colors.primaryBackground : colors.transparent)};

	:hover {
		background-color: ${colors.primaryBackground};
	}
`;

const CancelFilterButton = styled.i<{ displayed: boolean }>`
	cursor: pointer;
	display: ${({ displayed }) => (displayed ? undefined : "none")};

	:hover {
		color: ${colors.primary} !important;
	}
`;

const StyledFilterPanel = styled(FilterPanel)`
	@media ${physioMeMobileBreakpoint} {
		position: absolute;
		inset: 0;
		max-width: unset;
		max-height: unset;
		height: 100vh;
	}
`;

// Styled tooltip to match onboarding look without tying to a specific onboarding step
const FilterGuideTooltip = styled(Tooltip)`
	padding: 0;
	.tippy-arrow {
		color: ${colors.onboardingPrimary};
	}
	.tippy-content {
		padding: 0;
	}
`;
